import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { MagnifyingGlass } from '@phosphor-icons/react';

/** P12.1 — Global PRC / SC / Charge# quick-jump combobox. Press `/` to focus. */
export default function GlobalQuickJump() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) return;
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open || !q || q.length < 2) { setItems([]); return; }
    let cancel = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/lookup', { params: { q } });
        if (!cancel) setItems(r?.data?.data?.items || []);
      } catch { if (!cancel) setItems([]); }
      finally { if (!cancel) setLoading(false); }
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, open]);

  const go = (it) => {
    setOpen(false); setQ('');
    if (it?.route) navigate(it.route);
  };

  return (
    <div className="relative w-[280px]" data-testid="global-quick-jump">
      <div className="relative">
        <MagnifyingGlass
          className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2"
          style={{ color: 'var(--text-muted)' }}
        />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActiveIdx(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (!open) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, items.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter' && items[activeIdx]) { go(items[activeIdx]); }
            else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
          }}
          placeholder='Jump to PRC / SC / Size / Charge #  (press "/")'
          className="w-full pl-7 pr-2 py-1.5 rounded text-[12.5px]"
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}
          data-testid="quick-jump-input"
          autoComplete="off"
        />
      </div>
      {open && (loading || items.length > 0 || q.length >= 2) && (
        <div
          className="absolute z-50 mt-1 w-[360px] right-0 rounded shadow-lg max-h-80 overflow-y-auto"
          style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-default)' }}
          data-testid="quick-jump-dropdown"
        >
          {loading && <div className="px-3 py-2 text-[12px]" style={{ color: 'var(--text-muted)' }}>Searching…</div>}
          {!loading && items.length === 0 && q.length >= 2 && (
            <div className="px-3 py-2 text-[12px]" style={{ color: 'var(--text-muted)' }}>No matches.</div>
          )}
          {!loading && items.map((it, idx) => (
            <button
              key={`${it.type}-${it.label}-${idx}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => go(it)}
              className="w-full text-left px-3 py-1.5 text-[12.5px] flex items-center gap-2"
              style={{
                color: 'var(--text-primary)',
                background: idx === activeIdx ? 'var(--bg-hover)' : 'transparent',
                borderBottom: '1px solid var(--border-subtle)',
              }}
              data-testid={`quick-jump-opt-${it.type}-${it.label}`}
            >
              <span
                className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider"
                style={{ background: 'var(--accent-primary)', color: 'var(--text-inverse)', fontFamily: 'var(--font-heading)' }}
              >
                {it.type}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{it.label}</span>
              <span className="text-[11px] ml-auto" style={{ color: 'var(--text-muted)' }}>{it.sub}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
